/**
 * Pure binning logic for the Negotiations dashboard.
 *
 * Extracted from `negotiations.tsx` so it can be unit-tested without React.
 *
 * Why this exists
 * ---------------
 * The original filter binned contracts strictly by `lifecycleState`:
 *
 *   - `out_for_negotiation`  → "Out for negotiation"
 *   - `counter_received`     → "Counters waiting on you"
 *   - `internal_review` / `legal_review` → "Almost ready to send"
 *
 * That model assumed lifecycle state and negotiation activity move in
 * lockstep. They don't. Per the lifecycle gate comments in
 * `api-server/src/routes/negotiation.ts` (lines 658–665), contracts in
 * `internal_review`, `legal_review`, *and* `counter_received` are all
 * "pre-send" states that can carry active negotiation rounds. A contract
 * can be in `legal_review` AND have a live counter waiting for you at the
 * round level — by design.
 *
 * The original filter dropped those from the "Counters waiting on you"
 * and "Out for negotiation" cards, so users with active negotiations
 * stuck in legal/internal review saw a confusing empty dashboard.
 *
 * Fix (PR-A, Tier 1)
 * ------------------
 * Use `openIssuesCount > 0` as a secondary signal of active negotiation
 * activity. If a contract is in a pre-send review state but has open
 * negotiation issues, treat it as actively negotiating and route it to
 * the right bucket:
 *
 *   - `counter_received` OR (review state + open issues + last actor was
 *     counterparty) → "Counters waiting on you"
 *   - `out_for_negotiation` OR (review state + open issues + last actor
 *     was us)        → "Out for negotiation"
 *   - review state with no open issues → "Almost ready to send"
 *
 * Note: `ContractListItem` doesn't yet expose `negotiationHolder`
 * (server has it; API schema doesn't). Until PR-D adds round-level state
 * to the list endpoint, we conservatively route "review state +
 * openIssuesCount > 0" contracts into "Counters waiting on you" because
 * that's the higher-priority/blocking bucket — surfacing the contract
 * there is safer than hiding it. PR-D will fix the precision.
 */

import type { ContractListItem } from "@workspace/api-client-react";

export interface NegotiationBuckets {
  /** Counter-proposal received from coach or representation — needs your attention. */
  counter: ContractListItem[];
  /** Out with counterparty — waiting on them to respond. */
  out: ContractListItem[];
  /** In review with no negotiation activity yet — staged for the next round. */
  upstream: ContractListItem[];
}

const ACTIVE_REVIEW_STATES = new Set([
  "internal_review",
  "legal_review",
]);

export function binContractsForNegotiations(
  contracts: ContractListItem[],
): NegotiationBuckets {
  const out: ContractListItem[] = [];
  const counter: ContractListItem[] = [];
  const upstream: ContractListItem[] = [];

  for (const c of contracts) {
    const state = (c as { lifecycleState?: string }).lifecycleState ?? "draft";
    const openIssues = (c as { openIssuesCount?: number }).openIssuesCount ?? 0;

    // Explicit lifecycle signals take precedence — they're the ground truth
    // when the lifecycle has been advanced past internal review.
    if (state === "out_for_negotiation") {
      out.push(c);
      continue;
    }
    if (state === "counter_received") {
      counter.push(c);
      continue;
    }

    // Pre-send review states: route based on whether there's negotiation
    // activity. Open issues = active negotiation in flight.
    if (ACTIVE_REVIEW_STATES.has(state)) {
      if (openIssues > 0) {
        // Conservative routing: surface in "Counters waiting on you" so
        // the user can't miss a contract that's actively negotiating
        // while parked in review. PR-D will refine this using
        // negotiationHolder (us vs counterparty) once exposed on
        // ContractListItem.
        counter.push(c);
      } else {
        upstream.push(c);
      }
    }
  }

  return { out, counter, upstream };
}
